import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Download,
  IndianRupee,
  Users,
  CreditCard,
  Banknote,
  Globe,
  Wallet,
  Clock,
  Home,
  Building2,
  Utensils,
  Coffee,
  Store,
  Building,
  ShoppingBag,
  AlertCircle,
} from "lucide-react";
import { CSVLink } from "react-csv";
import { useCloud } from "../../hooks/useCloud";
import { SUCCESS } from "../../assets/constants";
import DateFilter from "../DateFilter";
import { observer } from "mobx-react-lite";
import dateStore from "../../hooks/useDate";
import { StackedProgressBar } from "../StackedProgressBar";
import ReportLoadingState from "./LoadingState";
import { GenericProgressChart } from "../Charts/GenericProgressChart";
import { GenericPieChart } from "../Charts/GenericPieChart";
import StatsCard from "../stats/StatsCard";
import PageHeader from "../PageHeader";

// Payment method specific colors
const PAYMENT_METHOD_COLORS = {
  online: "#16a34a", // vivid green
  zomato: "#e11d48", // bright crimson pink-red
  swiggy: "#f97316", // vivid orange
  cash: "#facc15", // bright yellow-gold
  card: "#7c3aed", // strong violet
  "swiggy/dineout": "#fb923c", // lighter orange
  pending: "#ef4444", // bright red
  "in-house": "#2563eb", // bold blue
  "zomato dining/district": "#be123c", // deep rose
  eazydiner: "#4f46e5", // indigo blue
  sodexo: "#0d9488", // teal green
  magicpin: "#db2777", // rose pink
  other: "#374151", // dark slate gray
  unsettled: "#ef4444", // Red
  cancelled: "#ef4444", // Red
  rejected: "#ef4444", // Red
};

// Payment method specific icons and colors
const PAYMENT_METHOD_CONFIG = {
  online: {
    icon: Globe,
    color: "text-emerald-600",
    bgColor: PAYMENT_METHOD_COLORS.online,
    label: "Online",
    order: 3,
  },
  zomato: {
    icon: Utensils,
    color: "text-red-600",
    bgColor: PAYMENT_METHOD_COLORS.zomato,
    label: "Zomato",
    order: 4,
  },
  swiggy: {
    icon: ShoppingBag,
    color: "text-orange-600",
    bgColor: PAYMENT_METHOD_COLORS.swiggy,
    label: "Swiggy",
    order: 5,
  },
  cash: {
    icon: Banknote,
    color: "text-amber-600",
    bgColor: PAYMENT_METHOD_COLORS.cash,
    label: "Cash",
    order: 1,
  },
  card: {
    icon: CreditCard,
    color: "text-purple-600",
    bgColor: PAYMENT_METHOD_COLORS.card,
    label: "Card",
    order: 2,
  },
  "swiggy/dineout": {
    icon: Coffee,
    color: "text-orange-600",
    bgColor: PAYMENT_METHOD_COLORS["swiggy/dineout"],
    label: "Swiggy/Dineout",
    order: 11,
  },
  pending: {
    icon: Clock,
    color: "text-red-900",
    bgColor: PAYMENT_METHOD_COLORS.pending,
    label: "Pending",
    order: 6,
  },
  "in-house": {
    icon: Home,
    color: "text-blue-600",
    bgColor: PAYMENT_METHOD_COLORS["in-house"],
    label: "In-House",
    order: 7,
  },
  "zomato dining/district": {
    icon: Building2,
    color: "text-red-600",
    bgColor: PAYMENT_METHOD_COLORS["zomato dining/district"],
    label: "Zomato Dining",
    order: 12,
  },
  eazydiner: {
    icon: Store,
    color: "text-indigo-600",
    bgColor: PAYMENT_METHOD_COLORS.eazydiner,
    label: "Eazydiner",
    order: 13,
  },
  sodexo: {
    icon: Building,
    color: "text-teal-600",
    bgColor: PAYMENT_METHOD_COLORS.sodexo,
    label: "Sodexo",
    order: 14,
  },
  magicpin: {
    icon: Wallet,
    color: "text-pink-600",
    bgColor: PAYMENT_METHOD_COLORS.magicpin,
    label: "Magicpin",
    order: 15,
  },
  other: {
    icon: Wallet,
    color: "text-gray-600",
    bgColor: PAYMENT_METHOD_COLORS.other,
    label: "Other",
    order: 16,
  },
  unsettled: {
    icon: AlertCircle,
    color: "text-red-600",
    bgColor: PAYMENT_METHOD_COLORS.unsettled,
    label: "Unsettled",
    order: 8,
  },
  cancelled: {
    icon: AlertCircle,
    color: "text-red-600",
    bgColor: PAYMENT_METHOD_COLORS.cancelled,
    label: "Cancelled",
    order: 9,
  },
  rejected: {
    icon: AlertCircle,
    color: "text-red-600",
    bgColor: PAYMENT_METHOD_COLORS.rejected,
    label: "Rejected",
    order: 10,
  },
};

// Extended color palette for more order types
const ORDER_TYPE_COLORS = {
  Dining: "#10B981", // Emerald
  Delivery: "#3B82F6", // Blue
  Takeaway: "#F59E0B", // Amber
  Pickup: "#8B5CF6", // Purple
  DriveThru: "#EC4899", // Pink
  Catering: "#06B6D4", // Cyan
  Event: "#EF4444", // Red
  Buffet: "#14B8A6", // Teal
  Corporate: "#6366F1", // Indigo
  Party: "#F472B6", // Pink
  Bulk: "#84CC16", // Lime
  Special: "#7C3AED", // Violet
  Regular: "#2DD4BF", // Teal
  Premium: "#F43F5E", // Rose
  Express: "#4F46E5", // Indigo
  Unsettled: "#EF4444", // Red
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Helper function to normalize payment method names
const normalizePaymentMethod = (method) => {
  if (!method) return "other";

  const normalized = method
    .toLowerCase()
    .replace(/[\s/-]/g, "_") // replace spaces, hyphens, and forward slashes with underscores
    .replace(/[^a-z0-9_]/g, ""); // remove any other special characters

  // Map of normalized names to standard keys
  const methodMap = {
    swiggy_dineout: "swiggy/dineout",
    swiggy_dine_out: "swiggy/dineout",
    zomato_dining: "zomato dining/district",
    zomato_dining_district: "zomato dining/district",
    eazy_diner: "eazydiner",
    eazydiner: "eazydiner",
    in_house: "in-house",
    pending_payment: "pending",
    settle_later: "pending",
  };

  return methodMap[normalized] || normalized;
};

// Update where payment methods are used
const getPaymentMethodConfig = (methodName) => {
  const normalizedMethod = normalizePaymentMethod(methodName);
  return PAYMENT_METHOD_CONFIG[normalizedMethod] || PAYMENT_METHOD_CONFIG.other;
};

// Helper function to check if a payment method should be included in distributions
const shouldIncludePaymentMethod = (methodName) => {
  const normalized = normalizePaymentMethod(methodName);
  return !["unsettled", "pending", "cancelled", "rejected", "in-house"].includes(
    normalized
  );
};

const Payments = observer(() => {
  const { dateType, startDate, endDate } = dateStore;
  const { orderTypeRevenueReport } = useCloud();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState({
    orderTypeData: [],
    total: [],
  });
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);

  // Add refs for controlling fetch behavior
  const fetchTimeoutRef = useRef(null);
  const isFirstRender = useRef(true);

  // Preprocess payment stats
  const { metrics, processedStats } = useMemo(() => {
    // Calculate metrics first
    const totalRevenue = paymentData.orderTypeData.reduce(
      (sum, type) => sum + Number(type.revenue),
      0
    );
    const totalOrders = paymentData.orderTypeData.reduce(
      (sum, type) => sum + Number(type.totalOrders),
      0
    );

    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Calculate digital vs cash payments - exclude unsettled
    const digitalPayments = paymentData.total
      .filter((method) => shouldIncludePaymentMethod(method.paymentMethodName))
      .filter((method) => ["online", "card"].includes(method.paymentMethodName))
      .reduce((sum, method) => sum + Number(method.amount), 0);

    const cashPayments =
      paymentData.total.find((method) => method.paymentMethodName === "cash")
        ?.amount || 0;

    // Calculate performance metrics
    const performance = {
      growth: 0,
      trend: "neutral",
      issues: 0,
    };

    // Calculate payment distribution - exclude unsettled
    const paymentDistribution = paymentData.total
      .filter((method) => shouldIncludePaymentMethod(method.paymentMethodName))
      .reduce((acc, method) => {
        acc[method.paymentMethodName] = {
          amount: Number(method.amount),
          percentage: ((Number(method.amount) / totalRevenue) * 100).toFixed(1),
        };
        return acc;
      }, {});

    const metrics = {
      totalRevenue,
      totalOrders,
      avgOrderValue,
      digitalPayments,
      cashPayments,
      performance,
      paymentDistribution,
    };

    // Now calculate the processed stats using the metrics
    const pendingAmount = paymentData.total
      .filter(
        (payment) =>
          normalizePaymentMethod(payment.paymentMethodName) === "pending"
      )
      .reduce((sum, payment) => sum + Number(payment.amount), 0);

    const unsettledAmount = paymentData.total
      .filter(
        (payment) =>
          normalizePaymentMethod(payment.paymentMethodName) === "unsettled"
      )
      .reduce((sum, payment) => sum + Number(payment.amount), 0);

    const inHouseAmount = paymentData.total
      .filter(
        (payment) =>
          normalizePaymentMethod(payment.paymentMethodName) === "in-house"
      )
      .reduce((sum, payment) => sum + Number(payment.amount), 0);

    const cancelledAmount = paymentData.total
      .filter(
        (payment) =>
          normalizePaymentMethod(payment.paymentMethodName) === "cancelled"
      )
      .reduce((sum, payment) => sum + Number(payment.amount), 0);

    const rejectedAmount = paymentData.total
      .filter(
        (payment) =>
          normalizePaymentMethod(payment.paymentMethodName) === "rejected"
      )
      .reduce((sum, payment) => sum + Number(payment.amount), 0);

    const pendingData = [
      {
        label: "Pending",
        value: pendingAmount,
        color: "#F59E0B",
      },
      {
        label: "Total",
        value: totalRevenue,
        color: "#E5E7EB",
      },
    ];

    const unsettledData = [
      {
        label: "Unsettled",
        value: unsettledAmount,
        color: "#EF4444",
      },
      {
        label: "Total",
        value: totalRevenue,
        color: "#E5E7EB",
      },
    ];

    const inHousePercentage = ((inHouseAmount / totalRevenue) * 100).toFixed(1);
    const pendingPercentage = ((pendingAmount / totalRevenue) * 100).toFixed(1);
    const unsettledPercentage = (
      (unsettledAmount / totalRevenue) *
      100
    ).toFixed(1);
    const cancelledPercentage = (
      (cancelledAmount / totalRevenue) *
      100
    ).toFixed(1);
    const rejectedPercentage = ((rejectedAmount / totalRevenue) * 100).toFixed(
      1
    );

    const processedStats = {
      pending: {
        amount: pendingAmount,
        percentage: pendingPercentage,
        data: pendingData,
        show: pendingAmount > 0,
      },
      unsettled: {
        amount: unsettledAmount,
        percentage: unsettledPercentage,
        data: unsettledData,
        show: unsettledAmount > 0,
      },
      inHouse: {
        amount: inHouseAmount,
        percentage: inHousePercentage,
        show: inHouseAmount > 0,
      },
      cancelled: {
        amount: cancelledAmount,
        percentage: cancelledPercentage,
        data: null,
        show: cancelledAmount > 0,
      },
      rejected: {
        amount: rejectedAmount,
        percentage: rejectedPercentage,
        data: null,
        show: rejectedAmount > 0,
      },
    };

    return { metrics, processedStats };
  }, [paymentData.total, paymentData.orderTypeData]);

  const fetchPaymentData = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
  }) => {
    try {
      setLoading(true);
      const response = await orderTypeRevenueReport({
        dateType: dateType,
        startDate: startDate,
        endDate: endDate,
      });

      if (response.status === SUCCESS) {
        setPaymentData(response.data);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (isFirstRender.current && startDate && endDate) {
      fetchPaymentData({});
      isFirstRender.current = false;
    }
  }, []);

  const handleDateChange = ({
    startDate: newStart,
    endDate: newEnd,
    dateType: newType,
  }) => {
    if (loading) return; // Prevent changes while loading
    fetchPaymentData({
      startDate: newStart,
      endDate: newEnd,
      dateType: newType,
    });
  };

  // Prepare progress chart data
  const progressChartData = paymentData.orderTypeData
    .map((type) => ({
      name: type.orderType,
      value: Number(((type.revenue / metrics.totalRevenue) * 100).toFixed(1)),
      fill: ORDER_TYPE_COLORS[type.orderType] || "#64748B",
      revenue: type.revenue,
      orders: type.totalOrders,
    }))
    .sort((a, b) => b.value - a.value); // Sort by percentage in descending order

  // Prepare chart data
  const orderTypeChartData = paymentData.orderTypeData.map((type) => ({
    label: type.orderType,
    value: Number(type.revenue),
    percentage: Number(type.percentOfTodaysRevenue),
    color: ORDER_TYPE_COLORS[type.orderType] || "#64748B",
  }));

  // Stats data for cards
  const stats = [
    {
      title: "Total Revenue",
      value: metrics.totalRevenue,
      prefix: "₹",
      subtitle: `${metrics.totalOrders} orders`,
      icon: IndianRupee,
      variant: "success",
    },
    {
      title: "Total Orders",
      value: metrics.totalOrders,
      subtitle: `Avg: ₹${metrics.avgOrderValue.toFixed(0)}`,
      icon: Users,
      variant: "primary",
    },
    {
      title: "Digital vs Cash",
      value:
        ((metrics.digitalPayments / metrics.totalRevenue) * 100).toFixed(1) +
        "%",
      trend: metrics.performance.growth,
      subtitle: "Digital Payment Share",
      icon: CreditCard,
      variant: "info",
    },
    {
      title: "Issues",
      value: metrics.performance.issues || 0,
      subtitle: "Payment Issues",
      icon: AlertCircle,
      variant: "danger",
    },
  ];

  if (loading) {
    return (
      <ReportLoadingState
        dateType={dateType}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        variant="payments"
        title="Payments Report"
        description="Analyze payment methods, revenue distribution, and order type performance"
      />
    );
  }

  // Helper function to get payment method display for order type
  const getOrderTypePaymentDisplay = (orderType, payments) => {
    return payments
      .filter((payment) => payment.amount > 0)
      .filter((payment) =>
        shouldIncludePaymentMethod(payment.paymentMethodName)
      )
      .filter(
        (payment) =>
          !selectedPaymentType ||
          payment.paymentMethodName.toLowerCase() === selectedPaymentType
      )
      .sort(
        (a, b) =>
          getPaymentMethodConfig(a.paymentMethodName).order -
          getPaymentMethodConfig(b.paymentMethodName).order
      )
      .map((payment) => {
        const config = getPaymentMethodConfig(payment.paymentMethodName);
        const Icon = config.icon;
        const percentage = ((payment.amount / orderType.revenue) * 100).toFixed(
          1
        );

        return (
          <div
            key={payment.paymentMethodName}
            className="bg-gray-50 rounded-lg p-3"
          >
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${config.color}`} />
              <div className="text-sm text-gray-600">{config.label}</div>
            </div>
            <div className="text-lg font-medium mt-1">
              ₹{Number(payment.amount).toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">
              {percentage}% of {orderType.orderType}
            </div>
          </div>
        );
      });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments Report"
        description="Analyze payment methods, revenue distribution, and order type performance"
        rightSlot={
          <DateFilter
            dateType={dateType}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            reportType={"order-type-revenue"}
          />
        }
        mobileDropdownContent={
          <DateFilter
            dateType={dateType}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            reportType={"order-type-revenue"}
          />
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Pending Card */}
        {processedStats.pending.show && (
          <StatsCard
            title="Pending Payments"
            icon={Clock}
            value={formatCurrency(processedStats.pending.amount)}
            subtitle="Awaiting Payment"
            variant="warning"
            showChart={true}
            data={processedStats.pending.data}
          />
        )}

        {/* Unsettled Card */}
        {processedStats.unsettled.show && (
          <StatsCard
            title="Unsettled Orders"
            icon={AlertCircle}
            value={formatCurrency(processedStats.unsettled.amount)}
            subtitle="Needs Settlement"
            variant="danger"
            showChart={true}
            data={processedStats.unsettled.data}
          />
        )}

        {/* In-House Card */}
        {processedStats.inHouse.show && (
          <StatsCard
            title="In-House Revenue"
            icon={Home}
            value={formatCurrency(processedStats.inHouse.amount)}
            subtitle={`${processedStats.inHouse.percentage}% of Total Revenue`}
            variant="primary"
          />
        )}
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Distribution Chart */}
        <div className="bg-white h-[350px] flex items-center justify-center">
          <div className="w-full h-full">
            <GenericPieChart
              data={progressChartData}
              title="Revenue Distribution"
              subtitle="By Order Type"
              dataKey="value"
              nameKey="name"
              tooltipFormatter={(value) => {
                const item = progressChartData.find((i) => i.value === value);
                if (!item) {
                  return `${formatCurrency(0)} (${value}%)`;
                }
                return `${formatCurrency(item.revenue)} (${value}%)`;
              }}
              tooltipTitle="Revenue Share"
              colors={Object.values(ORDER_TYPE_COLORS)}
              innerRadius="40%"
              outerRadius="90%"
              showLegend={true}
              startAngle={180}xs
              endAngle={0}
              legendPosition="bottom"
              centerLabel={{
                value: formatCurrency(metrics.totalRevenue),
                label: "Total Revenue",
              }}
            />
          </div>
        </div>

        {/* Payment Methods Analysis */}
        <div className="bg-white p-6 rounded-lg border border-gray-300">
          {/* Revenue Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {/* Total Revenue */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-indigo-600" />
                <span className="text-xs text-gray-600">Total Revenue</span>
              </div>
              <div className="text-lg font-semibold mt-1">
                {formatCurrency(metrics.totalRevenue)}
              </div>
              <div className="text-xs text-gray-500">
                {metrics.totalOrders} orders
              </div>
            </div>

            {/* Cancelled Revenue */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-xs text-gray-600">Cancelled Revenue</span>
              </div>
              <div className="text-lg font-semibold mt-1">
                {formatCurrency(processedStats.cancelled.amount)}
              </div>
              <div className="text-xs text-gray-500">
                {processedStats.cancelled.percentage}% of revenue
              </div>
            </div>
            {/* Rejected Revenue */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-xs text-gray-600">Rejected Revenue</span>
              </div>
              <div className="text-lg font-semibold mt-1">
                {formatCurrency(processedStats.rejected.amount)}
              </div>
              <div className="text-xs text-gray-500">
                {processedStats.rejected.percentage}% of revenue
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-300 my-4"></div>

          {/* Average Order Values */}
          <div className="mb-4">
            <h4 className="text-xs font-medium text-gray-600 mb-3">
              Average Order Values
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {paymentData.orderTypeData.map((type) => (
                <div key={type.orderType} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full bg-${
                        ORDER_TYPE_COLORS[type.orderType] === "#10B981"
                          ? "emerald"
                          : ORDER_TYPE_COLORS[type.orderType] === "#3B82F6"
                          ? "blue"
                          : "amber"
                      }-500`}
                    />
                    <span className="text-xs text-gray-600">
                      Avg {type.orderType}
                    </span>
                  </div>
                  <div className="text-lg font-semibold mt-1">
                    {formatCurrency(Number(type.averagePrice))}
                  </div>
                  <div className="text-xs text-gray-500">
                    {type.totalOrders} orders
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      {paymentData.total.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-300 p-6">
          <h4 className="text-base font-medium text-gray-800 mb-4">
            Payment Methods
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {paymentData.total
              .filter((method) =>
                shouldIncludePaymentMethod(method.paymentMethodName)
              )
              .sort((a, b) => Number(b.amount) - Number(a.amount))
              .map((method) => {
                const config = getPaymentMethodConfig(method.paymentMethodName);
                const percentage = (
                  (method.amount / metrics.totalRevenue) *
                  100
                ).toFixed(1);
                const Icon = config.icon;
                return (
                  <div
                    key={method.paymentMethodName}
                    className="bg-gray-50 hover:bg-gray-100 transition-colors rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`w-5 h-5 ${config.color}`} />
                      <span className="text-sm text-gray-700 font-medium">
                        {config.label}
                      </span>
                    </div>
                    <div className="text-xl font-semibold text-gray-900">
                      ₹{Number(method.amount).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {percentage}% of revenue
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Order Type Analysis */}
      <div className="bg-white rounded-lg border border-gray-300">
        <div className="p-6 border-b border-gray-300">
          <h3 className="text-lg font-semibold">Order Type Analysis</h3>
          <p className="text-sm text-gray-500">
            Payment method distribution by order type
          </p>

          {/* Payment Type Legend/Filter */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedPaymentType(null)}
              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                selectedPaymentType === null
                  ? "bg-gray-100 border-gray-300"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
            >
              All Types
            </button>
            {Object.entries(PAYMENT_METHOD_CONFIG).map(([key, config]) => {
              // Get total amount for this payment type across all order types
              const totalAmount = paymentData.orderTypeData.reduce(
                (sum, orderType) => {
                  const payment = orderType.payments.find(
                    (p) => normalizePaymentMethod(p.paymentMethodName) === key
                  );
                  return sum + (payment ? Number(payment.amount) : 0);
                },
                0
              );

              // Only show payment types that have transactions
              if (totalAmount === 0) return null;

              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedPaymentType(key)}
                  className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1.5 transition-colors ${
                    selectedPaymentType === key
                      ? `${config.color} border-current`
                      : "text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{config.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {paymentData.orderTypeData.map((orderType) => (
            <div key={orderType.orderType} className="mb-8 last:mb-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const methodConfig = getPaymentMethodConfig(
                        orderType.orderType.toLowerCase()
                      );
                      const Icon = methodConfig.icon;
                      return (
                        <Icon className={`w-5 h-5 ${methodConfig.color}`} />
                      );
                    })()}
                    <h4 className="text-base font-medium">
                      {orderType.orderType}
                    </h4>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <p className="text-sm text-gray-500">
                      {orderType.totalOrders} orders
                    </p>
                    <p className="text-sm text-gray-500">
                      Avg. {formatCurrency(orderType.averagePrice)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {orderType.percentOfTodaysRevenue.toFixed(1)}% of total
                      revenue
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold">
                    {formatCurrency(orderType.revenue)}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <StackedProgressBar
                  segments={orderType.payments
                    .filter((payment) => payment.amount > 0)
                    .filter((payment) =>
                      shouldIncludePaymentMethod(payment.paymentMethodName)
                    )
                    .filter(
                      (payment) =>
                        !selectedPaymentType ||
                        normalizePaymentMethod(payment.paymentMethodName) ===
                          selectedPaymentType
                    )
                    .map((payment) => {
                      const config = getPaymentMethodConfig(
                        payment.paymentMethodName
                      );
                      return {
                        label: config.label,
                        value: payment.amount,
                        tooltipValue: formatCurrency(payment.amount),
                        color: config.bgColor,
                      };
                    })}
                  total={
                    selectedPaymentType
                      ? orderType.payments.find(
                          (p) =>
                            normalizePaymentMethod(p.paymentMethodName) ===
                            selectedPaymentType
                        )?.amount || 0
                      : orderType.revenue
                  }
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {getOrderTypePaymentDisplay(orderType, orderType.payments)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default Payments;
